import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { HrApprovalKind } from '../../../shared/models/hr.model';
import {
  cancelHrLeaveRequest,
  cancelHrWfhRequest,
  decideHrApproval,
  getHrApprovals,
  getHrLeaveRequests,
  getHrLeaveTypes,
  getHrProfile,
  getHrWfhRequests,
  isNotLinked
} from '../../../shared/services/data/hr.data';
import {
  HrApprovalsUiModel,
  HrLeaveRequestUiModel,
  HrLeaveTypeUiModel,
  HrProfileUiModel,
  HrWfhRequestUiModel
} from '../../../shared/ui-models/hr.ui-model';
import { birdToast } from '../../../shared/components/core-ui/toast';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import { LoadingData } from '../../../ui/LoadingData';
import { HrDialog } from '../../shared/hr-dialog.component';
import { LeaveRequestModal } from '../../shared/leave-request-modal.component';
import { WfhRequestModal } from '../../shared/wfh-request-modal.component';
import { RejectModal } from '../../shared/reject-modal.component';
import { LeaveRequestsTable, WfhRequestsTable } from '../../shared/request-tables.component';
import {
  BTN_DANGER,
  BTN_PRIMARY,
  BTN_SECONDARY,
  BTN_SMALL,
  BTN_SMALL_DANGER,
  BTN_SMALL_PRIMARY,
  canCancel,
  CONTENT,
  EMPTY,
  formatRange,
  PAGE,
  PAGE_TITLE,
  SECTION_TITLE
} from '../../shared/hr.util';

type Tab = 'requests' | 'approvals';

type CancelTarget =
  | { kind: 'leave'; row: HrLeaveRequestUiModel }
  | { kind: 'wfh'; row: HrWfhRequestUiModel };

type RejectTarget = { kind: HrApprovalKind; id: string; name: string };

const routes = () => APP_ROUTES.HR_MODULE.CHILDREN as RouteConfig;

export const MyHrContainer: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const tab: Tab = location.pathname.startsWith(routes().APPROVALS.FULL_PATH)
    ? 'approvals'
    : 'requests';

  const [profile, setProfile] = useState<HrProfileUiModel | undefined>();
  const [profileError, setProfileError] = useState<ErrorInfo | undefined>();

  const [leaveTypes, setLeaveTypes] = useState<HrLeaveTypeUiModel[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<HrLeaveRequestUiModel[]>([]);
  const [wfhRequests, setWfhRequests] = useState<HrWfhRequestUiModel[]>([]);
  const [approvals, setApprovals] = useState<HrApprovalsUiModel>({ leave: [], wfh: [] });
  const [listsLoading, setListsLoading] = useState(true);
  const [listsError, setListsError] = useState<string | undefined>();

  const [leaveOpen, setLeaveOpen] = useState(false);
  const [wfhOpen, setWfhOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | undefined>();
  const [rejectTarget, setRejectTarget] = useState<RejectTarget | undefined>();
  /** The row an action is in flight for, so its buttons disable and nothing double-sends. */
  const [busyId, setBusyId] = useState<string | undefined>();

  const loadProfile = useCallback(() => {
    setProfileError(undefined);
    setProfile(undefined);
    getHrProfile().then((result) => {
      if (isErrorInfo(result)) {
        if (isNotLinked(result)) setProfile({ linked: false, isManager: false });
        else setProfileError(result);
        return;
      }
      setProfile(result);
    });
  }, []);

  useEffect(loadProfile, [loadProfile]);

  const linked = !!profile?.linked;
  const isManager = !!profile?.isManager;

  const loadLists = useCallback(() => {
    if (!linked) return Promise.resolve();
    setListsLoading(true);
    const mine =
      tab === 'requests'
        ? Promise.all([getHrLeaveTypes(), getHrLeaveRequests(), getHrWfhRequests()])
        : Promise.resolve(null);
    // Managers always fetch approvals: the tab carries the pending count.
    const pending = isManager ? getHrApprovals() : Promise.resolve(null);
    return Promise.all([mine, pending]).then(([own, approvalResult]) => {
      let error: ErrorInfo | undefined;
      if (own) {
        const [types, leave, wfh] = own;
        if (isErrorInfo(types)) error = types;
        else setLeaveTypes(types);
        if (isErrorInfo(leave)) error = error || leave;
        else setLeaveRequests(leave);
        if (isErrorInfo(wfh)) error = error || wfh;
        else setWfhRequests(wfh);
      }
      if (approvalResult) {
        if (isErrorInfo(approvalResult)) {
          if (tab === 'approvals') error = error || approvalResult;
        } else {
          setApprovals(approvalResult);
        }
      }
      setListsError(error ? error.message || describeLoadError(error) : undefined);
      setListsLoading(false);
    });
  }, [linked, isManager, tab]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const toastError = (error: ErrorInfo) =>
    birdToast.error(error.message || t('HR_MODULE.TOASTS.FAILED'), t('HR_MODULE.TOASTS.TITLE'));

  const confirmCancel = () => {
    if (!cancelTarget) return;
    const { kind, row } = cancelTarget;
    setBusyId(row.id);
    (kind === 'leave' ? cancelHrLeaveRequest(row.id) : cancelHrWfhRequest(row.id)).then(
      (result) => {
        setBusyId(undefined);
        if (isErrorInfo(result)) {
          toastError(result);
          return;
        }
        setCancelTarget(undefined);
        birdToast.success(t('HR_MODULE.TOASTS.CANCELLED'), t('HR_MODULE.TOASTS.TITLE'));
        loadLists();
      }
    );
  };

  const decide = (
    kind: HrApprovalKind,
    id: string,
    decision: 'approve' | 'reject',
    reason?: string
  ) => {
    setBusyId(id);
    decideHrApproval(kind, id, decision, reason).then((result) => {
      setBusyId(undefined);
      if (isErrorInfo(result)) {
        toastError(result);
        return;
      }
      if (decision === 'reject') setRejectTarget(undefined);
      birdToast.success(
        t(decision === 'approve' ? 'HR_MODULE.TOASTS.APPROVED' : 'HR_MODULE.TOASTS.REJECTED'),
        t('HR_MODULE.TOASTS.TITLE')
      );
      loadLists();
    });
  };

  if (profileError) {
    return (
      <div className={PAGE}>
        <div className={`${CONTENT} pt-[32px]`}>
          <LoadError
            message={profileError.message || t('HR_MODULE.LOAD_ERROR')}
            onRetry={loadProfile}
          />
        </div>
      </div>
    );
  }

  if (!profile) return <LoadingData />;

  if (!profile.linked) {
    return (
      <div className={PAGE}>
        <div className={`${CONTENT} pt-[32px]`}>
          <h1 className={`${PAGE_TITLE} mb-[24px]`}>{t('HR_MODULE.TITLE')}</h1>
          <div className={EMPTY}>
            <p className="m-0 mb-[8px] font-gilroy-bold text-neutral-500">
              {t('HR_MODULE.NOT_LINKED.TITLE')}
            </p>
            <p className="m-0">{t('HR_MODULE.NOT_LINKED.MESSAGE')}</p>
          </div>
        </div>
      </div>
    );
  }

  // A direct link to Approvals by someone with no reports lands on their own requests.
  if (tab === 'approvals' && !isManager) return <Redirect to={routes().REQUESTS.FULL_PATH} />;

  const pendingCount = approvals.leave.length + approvals.wfh.length;
  const tabs: { key: Tab; label: string; path: string }[] = [
    { key: 'requests', label: t('HR_MODULE.TABS.REQUESTS'), path: routes().REQUESTS.FULL_PATH },
    ...(isManager
      ? [
          {
            key: 'approvals' as Tab,
            label: t('HR_MODULE.TABS.APPROVALS'),
            path: routes().APPROVALS.FULL_PATH
          }
        ]
      : [])
  ];

  // WFH: only a pending request can be withdrawn — Horilla has no employee-side cancel for an
  // approved work-type request (the BFF answers 4xx), so the manager or HR has to undo it.
  const cancelAction = (target: CancelTarget) =>
    (target.kind === 'wfh' ? target.row.status === 'requested' : canCancel(target.row)) ? (
      <button
        type="button"
        className={BTN_SMALL}
        disabled={busyId === target.row.id}
        onClick={() => setCancelTarget(target)}
      >
        {t('HR_MODULE.ACTIONS.CANCEL')}
      </button>
    ) : null;

  const approvalActions = (kind: HrApprovalKind, id: string, name: string) => (
    <span className="inline-flex gap-[6px]">
      <button
        type="button"
        className={BTN_SMALL_PRIMARY}
        disabled={busyId === id}
        onClick={() => decide(kind, id, 'approve')}
      >
        {t('HR_MODULE.ACTIONS.APPROVE')}
      </button>
      <button
        type="button"
        className={BTN_SMALL_DANGER}
        disabled={busyId === id}
        onClick={() => setRejectTarget({ kind, id, name })}
      >
        {t('HR_MODULE.ACTIONS.REJECT')}
      </button>
    </span>
  );

  const renderRequests = () => (
    <>
      <section>
        <h2 className={SECTION_TITLE}>{t('HR_MODULE.BALANCES.TITLE')}</h2>
        {leaveTypes.length === 0 ? (
          <div className={EMPTY}>{t('HR_MODULE.BALANCES.EMPTY')}</div>
        ) : (
          <div className="grid grid-cols-1 gap-[12px] sm:grid-cols-2 lg:grid-cols-4">
            {leaveTypes.map((type) => (
              <div
                key={type.id}
                className="flex flex-col gap-[6px] rounded-[16px] border border-gray-200 bg-neutral-0 p-[16px]"
              >
                <span className="inline-flex items-center gap-[8px] text-[14px] font-gilroy-medium">
                  <span
                    className="inline-block size-[10px] shrink-0 rounded-full"
                    style={{ backgroundColor: type.color }}
                  />
                  {type.name}
                </span>
                <span className="text-[28px] leading-none font-gilroy-bold">
                  {type.availableDays}
                  <span className="ml-[6px] text-[13px] font-gilroy-regular text-neutral-300">
                    {t('HR_MODULE.BALANCES.AVAILABLE')}
                  </span>
                </span>
                <span className="text-[12px] text-neutral-300">
                  {t('HR_MODULE.BALANCES.TOTAL', { days: type.totalDays })}
                  {type.carryforwardDays > 0
                    ? ` · ${t('HR_MODULE.BALANCES.CARRYFORWARD', { days: type.carryforwardDays })}`
                    : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
      <section>
        <h2 className={SECTION_TITLE}>{t('HR_MODULE.LEAVE.TITLE')}</h2>
        {leaveRequests.length === 0 ? (
          <div className={EMPTY}>{t('HR_MODULE.LEAVE.EMPTY')}</div>
        ) : (
          <LeaveRequestsTable
            rows={leaveRequests}
            renderActions={(row) => cancelAction({ kind: 'leave', row })}
          />
        )}
      </section>
      <section>
        <h2 className={SECTION_TITLE}>{t('HR_MODULE.WFH.TITLE')}</h2>
        {wfhRequests.length === 0 ? (
          <div className={EMPTY}>{t('HR_MODULE.WFH.EMPTY')}</div>
        ) : (
          <WfhRequestsTable
            rows={wfhRequests}
            renderActions={(row) => cancelAction({ kind: 'wfh', row })}
          />
        )}
      </section>
    </>
  );

  const renderApprovals = () =>
    pendingCount === 0 ? (
      <div className={EMPTY}>{t('HR_MODULE.APPROVALS.EMPTY')}</div>
    ) : (
      <>
        {approvals.leave.length > 0 ? (
          <section>
            <h2 className={SECTION_TITLE}>{t('HR_MODULE.APPROVALS.LEAVE_TITLE')}</h2>
            <LeaveRequestsTable
              rows={approvals.leave}
              showEmployee={true}
              renderActions={(row) => approvalActions('leave', row.id, row.employee?.name || '')}
            />
          </section>
        ) : null}
        {approvals.wfh.length > 0 ? (
          <section>
            <h2 className={SECTION_TITLE}>{t('HR_MODULE.APPROVALS.WFH_TITLE')}</h2>
            <WfhRequestsTable
              rows={approvals.wfh}
              showEmployee={true}
              renderActions={(row) => approvalActions('wfh', row.id, row.employee?.name || '')}
            />
          </section>
        ) : null}
      </>
    );

  return (
    <div className={PAGE}>
      <div className={`${CONTENT} pt-[32px] pb-[24px]`}>
        <div className="flex flex-col justify-between gap-[16px] md:flex-row md:items-center">
          <div className="flex min-w-0 flex-col gap-[4px]">
            <h1 className={PAGE_TITLE}>{t('HR_MODULE.TITLE')}</h1>
            <span className="text-[14px] text-neutral-300">
              {profile.employee?.name}
              {profile.reportingManager
                ? ` · ${t('HR_MODULE.REPORTS_TO', { name: profile.reportingManager.name })}`
                : ''}
            </span>
          </div>
          {tab === 'requests' ? (
            <div className="flex shrink-0 flex-wrap items-center gap-[6px]">
              <button type="button" className={BTN_SECONDARY} onClick={() => setWfhOpen(true)}>
                {t('HR_MODULE.ACTIONS.NEW_WFH')}
              </button>
              <button type="button" className={BTN_PRIMARY} onClick={() => setLeaveOpen(true)}>
                {t('HR_MODULE.ACTIONS.NEW_LEAVE')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {tabs.length > 1 ? (
        <nav className="sticky top-0 z-40 border-b border-gray-200 bg-neutral-0">
          <div className={`${CONTENT} flex gap-[28px] overflow-x-auto [scrollbar-width:none]`}>
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => history.push(item.path)}
                className={`flex items-center gap-[8px] whitespace-nowrap border-b-2 py-[16px] text-[14px] font-gilroy-medium ${
                  tab === item.key
                    ? 'border-neutral-500 text-neutral-500'
                    : 'border-transparent text-neutral-300 hover:text-neutral-500'
                }`}
              >
                {item.label}
                {item.key === 'approvals' && pendingCount > 0 ? (
                  <span className="grid h-[20px] min-w-[20px] place-items-center rounded-full bg-primary-600 px-[4px] text-[10px] font-gilroy-bold text-neutral-0">
                    {pendingCount}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </nav>
      ) : null}

      <div className={`${CONTENT} flex flex-col gap-[32px] pt-[32px] pb-[80px]`}>
        {listsLoading ? (
          <LoadingData />
        ) : listsError ? (
          <LoadError message={listsError} onRetry={loadLists} />
        ) : tab === 'approvals' ? (
          renderApprovals()
        ) : (
          renderRequests()
        )}
      </div>

      {leaveOpen ? (
        <LeaveRequestModal
          leaveTypes={leaveTypes}
          onClose={() => setLeaveOpen(false)}
          onCreated={() => {
            setLeaveOpen(false);
            loadLists();
          }}
        />
      ) : null}

      {wfhOpen ? (
        <WfhRequestModal
          onClose={() => setWfhOpen(false)}
          onCreated={() => {
            setWfhOpen(false);
            loadLists();
          }}
        />
      ) : null}

      {cancelTarget ? (
        <HrDialog
          title={t('HR_MODULE.CANCEL_CONFIRM.TITLE')}
          onClose={() => setCancelTarget(undefined)}
          footer={
            <>
              <button
                type="button"
                className={BTN_SECONDARY}
                onClick={() => setCancelTarget(undefined)}
              >
                {t('HR_MODULE.ACTIONS.KEEP')}
              </button>
              <button
                type="button"
                className={BTN_DANGER}
                disabled={busyId === cancelTarget.row.id}
                onClick={confirmCancel}
              >
                {t('HR_MODULE.ACTIONS.CANCEL')}
              </button>
            </>
          }
        >
          <p className="m-0 text-[14px]">
            {cancelTarget.kind === 'leave'
              ? t('HR_MODULE.CANCEL_CONFIRM.LEAVE', {
                  type: cancelTarget.row.leaveTypeName,
                  dates: formatRange(cancelTarget.row.startDate, cancelTarget.row.endDate)
                })
              : t('HR_MODULE.CANCEL_CONFIRM.WFH', {
                  dates: formatRange(cancelTarget.row.startDate, cancelTarget.row.endDate)
                })}
          </p>
        </HrDialog>
      ) : null}

      {rejectTarget ? (
        <RejectModal
          employeeName={rejectTarget.name}
          saving={busyId === rejectTarget.id}
          onClose={() => setRejectTarget(undefined)}
          onConfirm={(reason) => decide(rejectTarget.kind, rejectTarget.id, 'reject', reason)}
        />
      ) : null}
    </div>
  );
};
